import { useCallback, useEffect, useState } from 'react';
import { createStore, createHook } from 'react-sweet-state';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src';
import { useIsAdmin, useLocale } from '@atlassian/jira-tenant-context-controller';
import { getProjectProperty } from '../services/get-project-property';
import { actions } from './actions';
import { LOADING, type State } from './types';

import {
	isInEligibleRoute,
	isNewProject as isNewProjectOld,
	DEFAULT_PROJECT_CREATION_START_DATE,
	calculateDaysDifference,
} from './utils';

type Actions = typeof actions;

const initialState: State = {
	panelState: LOADING,
	shouldRenderReopenSpotlight: false,
	shouldReopenPanel: false,
	votedCards: [],
};

const Store = createStore<State, Actions>({
	name: 'jsm-better-in-product-guidance-store',
	initialState,
	actions,
});

export const useRenderControllerState = createHook(Store);

export const useBetterInProductGuidanceController = (): {
	isEligibleAndInExperiment: boolean;
	dayssSinceProjectCreated: number;
	isLoading: boolean;
} => {
	const locale = useLocale();
	const isEnglishLocale = locale.startsWith('en');
	const isAdmin = useIsAdmin();
	const route = useCurrentRoute();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isNewProjectNew, setIsNewProjectNew] = useState<boolean>(false);
	const [cohortValue, setCohortValue] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const projectContext = useProjectContext();
	const projectContextData = projectContext?.data;
	const experimentProjectCreationStartDate = new Date(
		getFeatureFlagValue(
			'jsm-bipg-experiment-project-creation-start-date_q4zw1',
			DEFAULT_PROJECT_CREATION_START_DATE,
		),
	);
	// isNewProjectOld is redundant with the isNewProjectNew check, will be cleaned up later
	// isNewProjectOld will always be true as experimentProjectCreationStartDate is set to an old date in LD
	const isEligible =
		isEnglishLocale &&
		isAdmin &&
		isNewProjectOld(experimentProjectCreationStartDate, projectContextData?.created) &&
		isInEligibleRoute(route);

	const isInExperimentCohort = useCallback(() => cohortValue === 'experiment', [cohortValue]);

	useEffect(() => {
		// fetch project property as the last eligibility check
		if (projectContextData) {
			if (isEligible) {
				getProjectProperty(projectContextData?.projectKey)
					.then((data) => {
						setIsNewProjectNew(data.value === 'OPEN');
						setIsLoading(false);
					})
					.catch((error) => {
						setIsLoading(false);
						// Error status code 404 means the project property hasn't been set, it's not an error
						error.statusCode !== 404 &&
							fireOperationalAnalytics(createAnalyticsEvent({}), 'getBIPGProjectProperty failed', {
								errorCode: error.statusCode,
							});
					});
			} else {
				// if we're here, that means we're not doing the project property check
				// and hence we are done
				setIsLoading(false);
			}
		} else {
			// we don't have projectContextData to evaluate, eligibility check not passed
			setIsLoading(false);
		}
	}, [createAnalyticsEvent, isEligible, projectContextData]);

	useEffect(() => {
		// fire statsig and custom exposure event when eligibility checks pass
		if (isEligible && isNewProjectNew) {
			const cohort = expVal('jsm_better_in_product_guidance', 'cohort', 'not-enrolled');
			if (cohort !== 'not-enrolled') {
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					'statsigExperiment exposed',
					'jsm_better_in_product_guidance',
					{ cohort },
				);
			}
			setCohortValue(cohort);
		}
	}, [createAnalyticsEvent, isEligible, isNewProjectNew]);

	const isEligibleAndInExperiment = isEligible && isNewProjectNew && isInExperimentCohort();

	const dayssSinceProjectCreated = projectContextData?.created
		? calculateDaysDifference(new Date(projectContextData.created).getTime(), Date.now())
		: 0;

	return {
		isEligibleAndInExperiment,
		dayssSinceProjectCreated,
		isLoading,
	};
};
